import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  SidebarComponent,
  SidebarMenuItem,
  SidebarCallToAction,
  SidebarFooterItem,
} from '../../shared/components/sidebar/sidebar.component';
import { WorkspaceContextService } from '../../services/workspace-context.service';

@Component({
  selector: 'app-org-admin-sidebar',
  standalone: true,
  imports: [SidebarComponent],
  templateUrl: './org-admin-sidebar.component.html',
  styleUrl: './org-admin-sidebar.component.scss',
})
export class OrgAdminSidebarComponent implements OnInit, OnDestroy {
  private readonly workspaceContext = inject(WorkspaceContextService);
  private workspaceSub?: Subscription;

  get headerLabel(): string {
    return this.workspaceContext.panelLabel('Org Admin');
  }

  menuItems: SidebarMenuItem[] = [];

  ngOnInit(): void {
    this.menuItems = this.buildMenuItems();
    this.workspaceSub = this.workspaceContext.activeId$.subscribe(() => {
      this.menuItems = this.buildMenuItems();
    });
  }

  ngOnDestroy(): void {
    this.workspaceSub?.unsubscribe();
  }

  private buildMenuItems(): SidebarMenuItem[] {
    const items = this.buildOwnerMenuItems()
      .map((item) => {
        if (item.label !== 'Manage Assets') return item;
        return {
          ...item,
          children: this.workspaceContext
            .filterManageAssetsChildren(item.children ?? [])
            .map((child) => this.withDelegateAssetRequestTabs(child)),
        };
      })
      .filter(
        (item) =>
          item.label !== 'Manage Assets' || (item.children?.length ?? 0) > 0,
      );
    if (!this.workspaceContext.isDelegateSession()) return items;
    const hidden = new Set(['My Projects', 'Delegation', 'Organisation']);
    return [
      ...items.filter(
        (item) =>
          !hidden.has(item.label) && this.isDelegateItemAllowed(item.label),
      ),
      {
        label: 'Delegated Access',
        route: '/org-admin/delegated-access',
        icon: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M22 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
      },
    ];
  }

  /** Data Access unlocks Asset Management. User Management unlocks Manage Users. */
  private isDelegateItemAllowed(label: string): boolean {
    if (label === 'Asset Management') {
      return this.workspaceContext.hasGrantedScope('data-access');
    }
    if (label === 'Manage Users') {
      return this.workspaceContext.hasGrantedScope('org-user-management');
    }
    return true;
  }

  private withDelegateAssetRequestTabs(
    child: SidebarMenuItem,
  ): SidebarMenuItem {
    if (child.label !== 'My Asset Requests') return child;
    const tabs: string[] = [];
    if (this.workspaceContext.canShowAssetRequestTab()) {
      tabs.push('assetRequest');
    }
    if (this.workspaceContext.canShowPublishAssetsTab()) {
      tabs.push('publishAssets');
    }
    return {
      ...child,
      queryParams: { tab: tabs[0] || 'assetRequest' },
      activeMatchQueryParams: { tab: tabs.length ? tabs : ['assetRequest'] },
    };
  }

  private buildOwnerMenuItems(): SidebarMenuItem[] {
    return [
      {
        label: 'Dashboard',
        route: '/org-admin/home',
        icon: '<path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
      },
      {
        label: 'My Activities',
        route: '/org-admin/activities',
        icon: '<line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="3" y1="18" x2="21" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
      },
      {
        label: 'Asset Management',
        route: '/org-admin/requests',
        icon: '<path d="M4 7C4 6.46957 4.21071 5.96086 4.58579 5.58579C4.96086 5.21071 5.46957 5 6 5H14L18 9V17C18 17.5304 17.7893 18.0391 17.4142 18.4142C17.0391 18.7893 16.5304 19 16 19H6C5.46957 19 4.96086 18.7893 4.58579 18.4142C4.21071 18.0391 4 17.5304 4 17V7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M14 5V9H18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M7 7C7 6.46957 7.21071 5.96086 7.58579 5.58579C7.96086 5.21071 8.46957 5 9 5H17L21 9V17C21 17.5304 20.7893 18.0391 20.4142 18.4142C20.0391 18.7893 19.5304 19 19 19H9C8.46957 19 7.96086 18.7893 7.58579 18.4142C7.21071 18.0391 7 17.5304 7 17V7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M17 5V9H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
      },
      {
        label: 'My Projects',
        route: '/org-admin/my-projects',
        // Folder/briefcase icon for projects
        icon: '<path d="M4 7C4 6.46957 4.21071 5.96086 4.58579 5.58579C4.96086 5.21071 5.46957 5 6 5H10L12 7H18C18.5304 7 19.0391 7.21071 19.4142 7.58579C19.7893 7.96086 20 8.46957 20 9V19C20 19.5304 19.7893 20.0391 19.4142 20.4142C19.0391 20.7893 18.5304 21 18 21H6C5.46957 21 4.96086 20.7893 4.58579 20.4142C4.21071 20.0391 4 19.5304 4 19V7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
      },
      {
        label: 'Delegation',
        icon: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M9 12l2 2 4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
        children: [
          {
            label: 'Manage Delegate',
            route: '/org-admin/delegate',
          },
          {
            label: 'Delegate Activities',
            route: '/org-admin/delegation-activities',
          },
        ],
      },
      {
        label: 'Manage Assets',
        icon: '<path d="M4 7C4 6.46957 4.21071 5.96086 4.58579 5.58579C4.96086 5.21071 5.46957 5 6 5H14L18 9V17C18 17.5304 17.7893 18.0391 17.4142 18.4142C17.0391 18.7893 16.5304 19 16 19H6C5.46957 19 4.96086 18.7893 4.58579 18.4142C4.21071 18.0391 4 17.5304 4 17V7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M14 5V9H18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
        children: [
          {
            label: 'Add Dataset',
            route: '/org-admin/add-dataset-new',
          },
          {
            label: 'Add Model',
            route: '/org-admin/add-model',
          },
          {
            label: 'separator',
          },
          {
            label: 'Assets',
            route: '/org-admin/manage-contributions',
          },
          {
            label: 'My Asset Requests',
            route: '/org-admin/access-requests',
            queryParams: { tab: 'assetRequest' },
            activeMatchQueryParams: { tab: ['assetRequest', 'publishAssets'] },
          },
          {
            label: 'Access Policy',
            route: '/org-admin/access-requests',
            queryParams: { tab: 'policies' },
          },
        ],
      },
      {
        label: 'Manage Users',
        icon: '<path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45768C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
        children: [
          {
            label: 'User Requests',
            route: '/org-admin/user-requests',
          },
          {
            label: 'Org. Users',
            route: '/org-admin/view-all-users',
          },
          {
            label: 'Org. User Activities',
            route: '/org-admin/organisation-activities',
          },
        ],
      },
      {
        label: 'Organisation',
        route: '/org-admin/data-provider-entity',
        icon: '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M12 2C15 6 15 18 12 22C9 18 9 6 12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
      },
    ];
  }

  callToAction: SidebarCallToAction | undefined = undefined;

  footerItems: SidebarFooterItem[] = [
    {
      label: 'Profile',
      route: '/org-admin/profile',
      icon: '<path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="7" r="4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
    },
    {
      label: 'Support',
      route: '/org-admin/support',
      icon: '<path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45768C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
    },
  ];
}
